import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Boxes,
  Camera,
  FileText,
  PackageCheck,
  Printer,
} from "lucide-react";
import React, { Suspense, useCallback, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { toast } from "sonner";

import * as ozon from "@/api/ozon_fbs";

/**
 * Причины, которые лечит ПОВТОР ШАГА. `blocked` сюда не входит: его
 * лечат в 1С или пересканом, и повтор упёрся бы в ту же причину.
 */
type RepairableIssue = Exclude<ozon.OzonIssue["issue"], "blocked">;

/** Подпись кнопки «Разбора» — по причине, одна запись на код. */
const FIX_LABEL: Record<RepairableIssue, string> = {
  not_shipped: "Повторить",
  no_label: "Повторить",
  not_attached: "Привязать к поставке",
  no_documents: "К поставке",
};
import { toastError } from "@/lib/api-errors";
import { Button } from "@/components/ui/button";
import type { NeedStatus } from "@/lib/order-check";
import { plural } from "@/lib/order-check";
import { STATUS_ROW, STATUS_TEXT } from "@/lib/scan-ui/status";
import { parseScan } from "@/lib/order-scan";
import { NO_CHARACTERISTIC } from "@/lib/scan-ui/characteristic-choice";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { ScanBanner, ScanBannerSlot } from "@/pages/fbs/ScanBanner";
import { useNavigate } from "react-router-dom";

import { apiErrorText, LoadState, useGoBack } from "@/lib/scan-ui/screen";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { GOODS_FORMATS } from "@/lib/scan-formats";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { useScanCapture } from "@/lib/use-scan-capture";
import { MarkErrorList, shipRefusal } from "@/pages/ozon/MarkErrors";
import { AutoShipBanners, LeaveGuard, useAutoShip } from "@/pages/ozon/autoship";
import { fmtDateTime } from "@/lib/format";

/**
 * Сбор отправлений Ozon (этап C плана docs/ozon-fbs-plan.md).
 *
 * ⚠️ Скан в Ozon не пишет: `exemplar/set` требует полный набор кодов
 * отправления, поэтому они копятся у нас и уезжают перед сборкой («Собрать»).
 *
 * ⚠️ Но скан НЕ мгновенный: опознание товара идёт через 1С — 4–5 запросов
 * подряд. Поэтому коды, пришедшие во время запроса, ставятся в очередь, а не
 * теряются.
 *
 * Поля ввода кода нет: работает глобальный перехват сканера, как во всех
 * ТСД-экранах портала.
 *
 * Режимов два (решение 30.09.2026). Основной список — только ОДИНОЧНЫЕ
 * отправления (одна штука): скан закрывает отправление сразу, и портал сам
 * собирает его в Ozon и печатает этикетку (`autoship.tsx`). Многотоварные —
 * блоком ниже: тап открывает отправление на этом же экране, скан уходит с его
 * `posting_id`, на скане, закрывшем его, — та же автосборка и возврат к списку.
 */
/**
 * Заголовок отказа по машинному коду.
 *
 * ⚠️ Один текст «Скан не принят» на восемь ситуаций заставлял бы кладовщика
 * читать мелкий шрифт, чтобы понять, что делать с товаром в руках. Сборка
 * различает так же.
 */
const REFUSAL_TITLE: Record<string, string> = {
  not_in_posting: "Товар не нужен",
  duplicate: "Код уже отсканирован",
  unknown_code: "Код не опознан",
  box_code: "Это короб",
  busy: "Занято другим сканером",
  wrong_base: "Другая база 1С",
  barcode_ambiguous: "Штрихкод у нескольких товаров в 1С",
  bad_cim: "Неверный код маркировки",
  mark_required: "Нужен код маркировки",
  in_multi_posting: "Товар — в отправлении из нескольких",
  posting_gone: "Отправление снято",
  posting_shipped: "Отправление уже собирается",
  posting_unavailable: "Отправление недоступно",
  autoship_not_started: "Сборка не запущена",
  scan_blocked: "Скан не принят",
};

/** Отказы, после которых режим отправления бессмыслен: возвращаемся к списку. */
const POSTING_MODE_EXIT = new Set(["posting_gone", "posting_shipped"]);

/** Тело 409 — или общий текст, если сервер ответил иначе. */
function refusalOf(err: unknown): ozon.OzonScanRefusal {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === "object" && "code" in detail) {
    return detail as ozon.OzonScanRefusal;
  }
  return { code: "error", detail: apiErrorText(err, "Не удалось записать скан") };
}

export function OzonScanPage(): React.ReactElement {
  const { cabinetId } = useParams<{ cabinetId: string }>();
  const cabinet = Number(cabinetId);
  const goBack = useGoBack("/ozon");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [result, setResult] = useState<ozon.OzonScanResult | null>(null);
  // Последний код и накопленные ответы на уточнения.
  //
  // ⚠️ Ответы НАКАПЛИВАЮТСЯ (характеристика, потом партия), а не заменяют друг
  // друга: неся только последний, экран спрашивал бы предыдущий по кругу —
  // ровно этим болела инвентаризация.
  const [pendingScan, setPendingScan] = useState<ozon.OzonScanPayload | null>(null);
  const [refusal, setRefusal] = useState<ozon.OzonScanRefusal | null>(null);
  // Коды, пришедшие, пока летел запрос. МАССИВ, а не одно место: третий скан
  // в полёте второго иначе молча затирал бы второй.
  const queued = useRef<string[]>([]);
  // ⚠️ «В полёте» — ref, а не `send.isPending`: `onSettled` зовётся из
  // замыкания прошлого рендера (там `isPending` ещё `true`), и досылка через
  // проверку `isPending` клала код обратно в очередь — он не уходил никогда.
  const inFlight = useRef(false);
  // Режим отправления: открытое многотоварное отправление, скан — в его строки.
  const [openPosting, setOpenPostingState] = useState<{ id: string; number: string } | null>(null);
  // ⚠️ Контекст режима для СКАНА берётся из ref в момент ОТПРАВКИ, а не
  // запоминается при сканировании. Скан, пришедший, пока летел скан,
  // закрывший многотоварное отправление, должен уйти в основной список: к его
  // отправке отправление уже закрыто и режим снят. Запомненный `posting_id`
  // увёз бы его в собранное отправление («уже отправлено на сборку»). Ref
  // обновляется синхронно в сеттере — до ре-рендера.
  const openPostingRef = useRef<{ id: string; number: string } | null>(null);
  const setOpenPosting = useCallback((v: { id: string; number: string } | null) => {
    openPostingRef.current = v;
    setOpenPostingState(v);
  }, []);

  const state = useQuery({
    // ⚠️ Кабинет — часть ключа: без него экран сбора и общий список делили
    // один кэш, и первый смонтировавшийся выигрывал — прогресс показывался
    // от чужого набора данных.
    queryKey: ["ozon", "pick-state", cabinet],
    queryFn: () => ozon.pickState(cabinet),
  });

  // ⚠️ Сборка НЕОБРАТИМА: обратной ручки у Ozon нет, откат — только отмена
  // заказа со штрафом. Поэтому кнопка спрашивает подтверждение, а не «жмётся
  // случайно рядом со сканом».
  const shipMut = useMutation({
    mutationFn: ozon.shipPosting,
    onSuccess: (updated) => {
      toast.success("Отправление собрано");
      setConfirmShip(null);
      putPosting(updated);
    },
    onMutate: () => setShipProblem(null),
    onError: (err) => {
      // ⚠️ Отказ по кодам маркировки — не «не удалось»: у него свой заголовок
      // и строка на каждый не принятый код. Показываем ПЛАШКОЙ в «Коробках»
      // (не тостом — он исчезает сам), рядом со строками, где у каждого
      // не принятого кода есть «Заменить код». «Разбор» закрываем: чинят там.
      const refusal = shipRefusal(err);
      if (!refusal) {
        toastError("Не удалось выполнить")(err);
        return;
      }
      setConfirmShip(null);
      setFixOpen(false);
      setShipProblem(refusal);
      setBoxesOpen(true);
      void qc.invalidateQueries({ queryKey: ["ozon", "postings"] });
    },
  });

  // «Заменить код» = снять не принятый Ozon скан; новую штуку сканируют
  // обычным путём, и её код займёт освободившийся слот экземпляра.
  const replaceMut = useMutation({
    mutationFn: ozon.deletePick,
    onSuccess: () => {
      toast.success("Скан снят — закройте «Коробки» и отсканируйте штуку заново");
      void qc.invalidateQueries({ queryKey: ["ozon", "postings"] });
      void qc.invalidateQueries({ queryKey: ["ozon", "pick-state"] });
    },
    onError: toastError("Не удалось снять скан"),
  });

  const labelMut = useMutation({
    mutationFn: ozon.printLabel,
    onSuccess: (updated) => {
      toast.success("Этикетка отправлена на принтер");
      putPosting(updated);
    },
    onError: toastError("Не удалось выполнить"),
  });

  // ⚠️ Кладём ОБНОВЛЁННУЮ строку, а не перезапрашиваем раздел. Обе ручки уже
  // вернули её. Прежний `invalidateQueries(["ozon"])` тянул заодно `pick-state`
  // (сборка на него не влияет — пиклист фильтрует по `gone_at`) и `config`
  // (статика), то есть ~88 КБ и два лишних похода на каждое нажатие.
  const putPosting = (updated: ozon.OzonPosting) => {
    qc.setQueryData<{ postings: ozon.OzonPosting[]; total: number }>(
      ["ozon", "postings", "cabinet", cabinet],
      (old) =>
        old && {
          ...old,
          postings: old.postings.map((p) => (p.id === updated.id ? updated : p)),
        },
    );
    // ⚠️ Шаг конвейера сдвинулся — значит сдвинулся и «Разбор»: строка либо
    // уходит из него, либо меняет причину на следующую. Инвалидируем ИМЕННО
    // его и `pick-state` (там живёт бейдж), а не весь раздел: ответ сюда
    // приходит со строкой, а `["ozon"]` целиком тянул бы ещё и статику.
    void qc.invalidateQueries({ queryKey: ["ozon", "issues"] });
    void qc.invalidateQueries({ queryKey: ["ozon", "pick-state"] });
  };

  // Автосборка+печать на скане, закрывшем отправление. После каждого шага —
  // та же запись в кеш, что у ручных мутаций; после отказа — перечитать
  // «Разбор» и пиклист (строки ответа у отказа нет).
  const autoShip = useAutoShip({
    onPosting: (p) => putPosting(p),
    onFailure: () => {
      void qc.invalidateQueries({ queryKey: ["ozon", "issues"] });
      void qc.invalidateQueries({ queryKey: ["ozon", "pick-state"] });
      void qc.invalidateQueries({ queryKey: ["ozon", "postings"] });
    },
  });

  // ⚠️ Комплект документов выписывается на ПОСТАВКУ — кучку собранных
  // отправлений, которые увезут за раз, — а не на каждое отправление. Ручка
  // принимает отправление и сама находит его поставку: «Разбор» знает
  // отправление, и разыскивать поставку экраном было бы его работой.
  const send = useMutation({
    mutationFn: ozon.scan,
    onSuccess: (data, variables) => {
      setResult(data);
      setRefusal(null);
      // ⚠️ Скан закрыл отправление — собираем и печатаем САМИ, без вопроса:
      // набрано ровно то, что ждёт Ozon, и спрашивать на каждой коробке
      // значило бы жать «Собрать» полсотни раз в день.
      if (data.ok && data.posting_complete) {
        if (variables.posting_id) setOpenPosting(null);
        if (!data.posting_id) {
          // Скан ЗАПИСАН — «не принят» было бы враньём; но собирать нечем.
          setRefusal({
            code: "autoship_not_started",
            detail:
              `Скан засчитан (${data.posting_number}), но ответ без id отправления — ` +
              "сборка не запущена, см. «Разбор».",
          });
        } else {
          autoShip.enqueue(data.posting_id, data.posting_number);
        }
      }
      void qc.invalidateQueries({ queryKey: ["ozon", "pick-state"] });
      // ⚠️ Список отправлений СЕЙЧАС не перечитывается: шторка сидит на
      // `enabled: boxesOpen`, а `invalidateQueries` по умолчанию обновляет
      // только активные наблюдатели. Строка не мёртвая — она помечает данные
      // устаревшими, и общий список `/ozon` перечитает их при следующем входе.
      // Без этой оговорки её либо снесут как лишнюю, либо снимут `enabled` и
      // получат 88 КБ на каждый скан.
      void qc.invalidateQueries({ queryKey: ["ozon", "postings"] });
    },
    onError: (err, variables) => {
      setResult(null);
      const r = refusalOf(err);
      if (variables.posting_id) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        // Режим отправления: оно снято / уже собирается / чужое — держать
        // его открытым незачем, текст бэка остаётся на плашке.
        if (status === 403 || status === 404) {
          setRefusal({ ...r, code: "posting_unavailable" });
          setOpenPosting(null);
          return;
        }
        if (POSTING_MODE_EXIT.has(r.code)) setOpenPosting(null);
      }
      setRefusal(r);
    },
    onSettled: () => {
      // Досылка — НАПРЯМУЮ (как `ShipmentPage`), без проверки «в полёте».
      const next = queued.current.shift();
      if (next === undefined) {
        inFlight.current = false;
        return;
      }
      dispatch(codePayload(next));
    },
  });

  /** Код → тело скана, без контекста режима (его добавляет `dispatch`). */
  const codePayload = (raw: string): ozon.OzonScanPayload => {
    const parsed = parseScan(raw);
    return {
      cabinet_id: cabinet,
      scan_kind: parsed.kind,
      raw: parsed.raw,
      ean13: parsed.ean13,
      gtin: parsed.gtin,
      serial: parsed.serial,
      batch: parsed.batch,
    };
  };

  /** Отправить скан в режиме, открытом СЕЙЧАС (см. `openPostingRef`). */
  const dispatch = (base: ozon.OzonScanPayload) => {
    const { posting_id: _stale, ...rest } = base;
    const open = openPostingRef.current;
    const payload: ozon.OzonScanPayload = open ? { ...rest, posting_id: open.id } : rest;
    inFlight.current = true;
    setPendingScan(payload);
    mutateRef.current(payload);
  };
  const mutateRef = useRef(send.mutate);
  mutateRef.current = send.mutate;
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const handleScan = useCallback(
    (raw: string) => {
      if (inFlight.current) {
        queued.current.push(raw);
        return;
      }
      dispatchRef.current(codePayload(raw));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cabinet],
  );

  /** Досылка того же кода с добавленным ответом — тоже в ТЕКУЩЕМ режиме. */
  const answer = useCallback(
    (patch: Partial<ozon.OzonScanPayload>) => {
      if (!pendingScan) return;
      dispatchRef.current({ ...pendingScan, ...patch });
    },
    [pendingScan],
  );

  const asking = result?.code === "needs_clarification";
  // ⚠️ Захват НЕ выключается на время запроса. Ровно это условие
  // (`!isPending`) уже стоило раздела отгрузки: пока запрос летел, КАЖДЫЙ скан
  // пропадал молча — ни ошибки, ни звука, ни строки. Со склада это выглядело
  // как «первый паллет считался, дальше никак» (07.09.2026). Здесь цена выше:
  // опознание идёт через 1С, 4–5 запросов, то есть окно потери — не миг.
  //
  // Пока запрос в полёте, код КОПИТСЯ и уходит сразу после ответа.
  // Выключаемся только на время ВОПРОСА: там следующий код должен стать
  // ответом, а не уехать в общий разбор.
  usePreloadChunk(CameraScanner);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [boxesOpen, setBoxesOpen] = useState(false);
  const [fixOpen, setFixOpen] = useState(false);
  // ⚠️ Подтверждение сборки — НАШ диалог, а не `window.confirm`.
  // Сканер работает клавиатурой и дописывает Enter, а Enter на нативном
  // окне = «ОК»: любой скан, пока оно висит, САМ подтвердил бы необратимую
  // сборку. Плюс кнопки под перчатку и аппаратная «назад» = «Отмена».
  const [confirmShip, setConfirmShip] = useState<ozon.OzonPosting | null>(null);
  const [shipProblem, setShipProblem] = useState<ReturnType<typeof shipRefusal>>(null);

  // ⚠️ Отправления грузятся ТОЛЬКО когда открыта шторка: список нужен для
  // вопроса «что паковать», а он возникает редко. В `pick-state` их нет
  // намеренно — та ручка зовётся после каждого скана.
  const boxes = useQuery({
    queryKey: ["ozon", "postings", "cabinet", cabinet],
    queryFn: () => ozon.listPostings({ cabinet_id: cabinet }),
    enabled: boxesOpen,
  });

  // ⚠️ Конфиг СВОЙ, а не приёмкин: чужая ручка требует права `receiving`, и
  // кладовщик с одним правом `ozon_fbs` не увидел бы кнопку камеры МОЛЧА.
  const configQ = useQuery({ queryKey: ["ozon", "config"], queryFn: ozon.config });
  const cameraAllowed = (configQ.data?.scan_method ?? "tsd") !== "tsd";

  // Перехват выключен только вопросом и окнами — не запросом (скан в полёте
  // копится в `queued`). Пойманный при них код не теряем молча, а говорим,
  // почему он не принят.
  const onBlocked = (raw: string) => {
    const reason = asking
      ? "Сначала ответьте на вопрос на экране, потом сканируйте."
      : cameraOpen
        ? "Открыта камера — закройте её, чтобы сканировать сканером."
        : boxesOpen
          ? "Сначала закройте список отправлений."
          : "Сначала закройте «Разбор».";
    setRefusal({ code: "scan_blocked", detail: `${reason} Код: ${raw}` });
  };
  const { scanInputRef, scanInputProps } = useScanCapture({
    // Пока открыта камера или задан вопрос, аппаратный перехват молчит: иначе
    // один код пришёл бы дважды, а ответ уехал бы в общий разбор.
    enabled: !asking && !cameraOpen && !boxesOpen && !fixOpen,
    onScan: handleScan,
    onBlocked,
  });
  // Аппаратная «назад» при открытой камере закрывает камеру, а не экран сбора.
  useBackGuard(cameraOpen, () => setCameraOpen(false));
  // «Назад» в режиме отправления — к списку, а не с экрана.
  useBackGuard(!!openPosting, () => setOpenPosting(null));

  const chainNow = autoShip.entries.find((e) => e.phase === "shipping" || e.phase === "printing");
  const multi = state.data?.multi_postings ?? [];
  const opened = openPosting ? multi.find((p) => p.id === openPosting.id) : undefined;

  return (
    <div className="flex h-app flex-col bg-slate-50">
      <header className="flex items-center gap-2 border-b bg-white px-3 py-2">
        <Button size="icon" variant="ghost" onClick={goBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">Сбор Ozon</div>
          <div className="text-xs text-muted-foreground">
            {state.data
              ? `осталось взять: ${state.data.units_left} из ${state.data.units_total} шт`
              : "…"}
          </div>
        </div>
        {/* ⚠️ Бейдж показывается, ТОЛЬКО когда есть что разбирать. Пустая
            кнопка в аппбаре скан-экрана — лишний повод её нажать, а места
            там на один тап. */}
        {!!state.data?.issues && (
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 border-amber-400 text-amber-700"
            onClick={() => setFixOpen(true)}
          >
            <AlertTriangle className="mr-1 size-4" /> Разбор {state.data.issues}
          </Button>
        )}
        {/* ⚠️ Отсюда только ПЕРЕХОД, как у ВБ. Завершение поставки — свой
            экран: это разбор и необратимые документы, а скан-экран живёт в
            один вьюпорт без скролла, и место на нём уходит тому, ради чего
            кладовщик его открыл, — списку к сбору. */}
        {state.data?.supply && state.data.supply.postings_total > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate(`/ozon/supplies/${state.data!.supply!.id}/finish`)}
          >
            Поставка {state.data.supply.postings_total}
          </Button>
        )}
      </header>

      <input ref={scanInputRef} {...scanInputProps} className="sr-only" />

      <main className="flex-1 overflow-y-auto p-3">
        {state.isLoading || state.isError ? (
          <LoadState
            isLoading={state.isLoading}
            error={state.error}
            onRetry={() => void state.refetch()}
          />
        ) : (
          <>
            <ScanBannerSlot>
              <div className="space-y-2">
              <AutoShipBanners
                entries={autoShip.entries}
                onDismiss={autoShip.dismiss}
                onOpenBoxes={(marks) => {
                  setShipProblem(marks);
                  setBoxesOpen(true);
                }}
              />
              {refusal ? (
                <ScanBanner
                  tone="error"
                  title={REFUSAL_TITLE[refusal.code] ?? "Скан не принят"}
                  onClose={() => setRefusal(null)}
                  actions={
                    refusal.code === "in_multi_posting" && refusal.postings?.length ? (
                      <>
                        {refusal.postings.map((p) => (
                          <Button
                            key={p.id}
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setRefusal(null);
                              setResult(null);
                              setOpenPosting({ id: p.id, number: p.posting_number });
                            }}
                          >
                            Открыть № {p.posting_number}
                          </Button>
                        ))}
                      </>
                    ) : undefined
                  }
                >
                  {refusal.detail}
                </ScanBanner>
              ) : send.isPending ? (
                <ScanBanner tone="info" title="Ищем в 1С…" onClose={null} />
              ) : result?.ok ? (
                <ScanBanner tone="ok" title="Принято" onClose={() => setResult(null)}>
                  <div className="font-medium">
                    {result.nomenclature_article ? `${result.nomenclature_article} · ` : ""}
                    {result.nomenclature_name}
                  </div>
                  {result.partia_name && <div>партия {result.partia_name}</div>}
                  <div className="mt-1">
                    {result.posting_number}: {result.posting_done} из {result.posting_total}
                    {result.posting_complete && " — набрано"}
                  </div>
                </ScanBanner>
              ) : null}
              </div>
            </ScanBannerSlot>
            {asking && result && (
              // ⚠️ `key` по коду: без него партия, набранная для товара A,
              // оставалась бы в вопросе про товар B с активной кнопкой.
              <ClarifySheet key={pendingScan?.raw} result={result} onAnswer={answer} />
            )}
            {/* ⚠️ Причина, по которой поставку не предлагают, показывается
                ТЕКСТОМ. Пустое место кладовщик прочитал бы как «ещё нечего
                закрывать» и ждал бы кнопку, которая не появится — а она не
                появится никогда, пока у кабинета две открытые поставки. */}
            {state.data?.supply_note && (
              <div className="mt-3">
                <ScanBanner tone="warn" title="Поставку закрыть нельзя" onClose={null}>
                  {state.data.supply_note}
                </ScanBanner>
              </div>
            )}

            {/* ⚠️ ГЛАВНОЕ — товары, а не отправления. Экран отвечает на
                «что нести с полки», и у ВБ на этом месте ровно такой список.
                Отправлениями он был до 10.09.2026: при 99,5 % единичек пять
                коробок одного товара выглядели пятью строками вместо одной
                понятной «нужно 5». */}
            {openPosting ? (
              <PostingMode
                number={openPosting.number}
                posting={opened}
                onBack={() => setOpenPosting(null)}
              />
            ) : (
              <>
                <ul className="mt-3 space-y-2">
                  {state.data?.rows.map((r) => (
                    <PickRow key={r.key} row={r} />
                  ))}
                </ul>
                {multi.length > 0 && (
                  <MultiPostingList
                    postings={multi}
                    onOpen={(p) => {
                      setRefusal(null);
                      setResult(null);
                      setOpenPosting({ id: p.id, number: p.posting_number });
                    }}
                  />
                )}
              </>
            )}

            {/* Отправления — вопрос «что паковать», а не «что нести». Он
                возникает реже, поэтому живёт в шторке. */}
            {!openPosting && (!!state.data?.rows.length || multi.length > 0) && (
              <Button
                variant="outline"
                className="mt-3 w-full"
                onClick={() => setBoxesOpen(true)}
              >
                <Boxes className="mr-1 size-4" /> Коробки
              </Button>
            )}

            {/* ⚠️ ПОСТАВКА — кучка собранных отправлений, которые увезут за
                раз, и на неё выписывается ОДИН комплект документов 1С. Блок
                появляется, только когда есть что закрывать: пустой он просил
                бы сделать шаг, для которого ещё нет работы. */}
          </>
        )}
      </main>

      {/* Кнопка в нижней панели, в зоне большого пальца, — не плавающая. */}
      {cameraAllowed && !cameraOpen && (
        <nav className="border-t bg-white p-3">
          <Button
            className="h-12 w-full bg-sky-700 hover:bg-sky-800"
            onClick={() => setCameraOpen(true)}
          >
            <Camera className="mr-1 size-4" /> Сканировать камерой
          </Button>
        </nav>
      )}

      {fixOpen && (
        <FixSheet
          cabinetId={cabinet}
          // ⚠️ ТЕ ЖЕ мутации, что у карточки отправления: свои означали бы
          // вторую копию их тостов и записи в кеш.
          repair={{
            // ⚠️ Обёртки, а не `shipMut.mutate` напрямую: у мутации второй
            // параметр — это опции react-query, и передав туда ссылку на
            // поставку, мы бы молча скормили ей чужой объект.
            not_shipped: (row: ozon.OzonIssue) => shipMut.mutate(row.posting_id),
            no_label: (row: ozon.OzonIssue) => labelMut.mutate(row.posting_id),
            // Собрано, но `attach` упал: повтор `/ship` второй сборки не шлёт
            // (`shipped_at` стоит), а доделывает опрос и привязку к поставке.
            // Бэк выводит эту причину ТОЛЬКО там, где повтор пройдёт.
            not_attached: (row: ozon.OzonIssue) => shipMut.mutate(row.posting_id),
            // Не выписываем отсюда: документы — на своём экране (см. подпись
            // кнопки в шторке).
            //
            // ⚠️ Ведём к поставке ЭТОГО отправления, а не к текущей открытой:
            // «Разбор» смотрит на кабинет целиком и показывает застрявшее из
            // прошлых поставок тоже, а переход в сегодняшнюю его не починил
            // бы. Поставку называет сама строка разбора.
            no_documents: (row: ozon.OzonIssue) =>
              navigate(`/ozon/supplies/${row.supply_id}/finish`),
          }}
          busy={shipMut.isPending || labelMut.isPending}
          onClose={() => setFixOpen(false)}
        />
      )}

      {/* max-h + прокрутка у шторки обязательны: панель прижата к низу, и без
          них верхние строки и кнопка «Закрыть» уезжают за экран — закрыть её
          можно было бы только тапом по фону. Так во всех шторках-списках
          портала. */}
      {boxesOpen && (
        <BottomSheet
          onClose={() => setBoxesOpen(false)}
          panelClassName="max-h-[75vh] overflow-y-auto p-3"
        >
          <div className="mb-2 font-medium">Коробки к отправке</div>
          {shipProblem && (
            <div className="mb-2">
              <ScanBanner tone="error" title={shipProblem.title} onClose={() => setShipProblem(null)}>
                <ul className="space-y-0.5">
                  {shipProblem.lines.map((l) => (
                    <li key={l} className="break-words">{l}</li>
                  ))}
                </ul>
              </ScanBanner>
            </div>
          )}
          {/* ⚠️ Список режется сервером на 200. Молчаливая обрезка читается как
              «больше коробок нет» — тот самый класс дефекта, ради которого эта
              правка и писалась, только у шапки он уже вылечен. Пока проекции
              «только незавершённые» нет, обрезку хотя бы НАЗЫВАЕМ. */}
          {!!boxes.data && boxes.data.total > boxes.data.postings.length && (
            <p className="mb-2 text-xs text-amber-700">
              показано {boxes.data.postings.length} из {boxes.data.total} — уточните
              поиском в списке отправлений
            </p>
          )}
          {/* ⚠️ Только под условием: у `LoadState` НЕТ ветки успеха — при
              `isLoading=false` он рисует ошибку. Безусловный вызов показывал
              красную плашку «Не удалось загрузить» поверх корректно
              загруженного списка. Так же обёрнуты все 12 остальных вызовов. */}
          {(boxes.isLoading || boxes.isError) && (
            <LoadState
              isLoading={boxes.isLoading}
              error={boxes.error}
              onRetry={() => void boxes.refetch()}
            />
          )}
          <ul className="space-y-2">
            {boxes.data?.postings.map((p) => (
              <PostingRow
                key={p.id}
                posting={p}
                onShip={() => setConfirmShip(p)}
                onLabel={() => labelMut.mutate(p.id)}
                onReplace={(pickId) => replaceMut.mutate(pickId)}
                replaceBusy={replaceMut.isPending}
                busy={
                  shipMut.variables === p.id || labelMut.variables === p.id
                }
              />
            ))}
          </ul>
          <Button variant="outline" className="mt-3 w-full" onClick={() => setBoxesOpen(false)}>
            Закрыть
          </Button>
        </BottomSheet>
      )}

      {confirmShip && (
        <Dialog
          title="Собрать в Ozon?"
          danger
          body={
            <>
              Отправление <b>{confirmShip.posting_number}</b> уедет ровно с тем,
              что в нём собрано. Отменить сборку нельзя — обратного действия у
              площадки нет, откат только через отмену заказа со штрафом.
            </>
          }
          confirmLabel="Собрать"
          confirmBusy={shipMut.isPending}
          onConfirm={() => shipMut.mutate(confirmShip.id)}
          onCancel={() => setConfirmShip(null)}
        />
      )}

      <LeaveGuard
        busy={autoShip.busy}
        current={chainNow ? `№ ${chainNow.postingNumber}` : "очередь отправлений"}
      />

      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-40 bg-black">
            <CameraScanner
              formats={GOODS_FORMATS}
              onDetect={(raw) => {
                setCameraOpen(false);
                handleScan(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}

/**
 * Строка «что нести с полки»: товар с количеством, как на экране сбора ВБ.
 *
 * ⚠️ Агрегат по (номенклатура, характеристика) считает СЕРВЕР — тем же ключом,
 * которым скан ищет место. Складывать на экране по артикулу нельзя: у ВБ это
 * уже стоило инцидента, один артикул несёт несколько размеров со своими
 * штрихкодами.
 */
function PickRow({ row }: { row: ozon.OzonPickRow }): React.ReactElement {
  // ⚠️ Состояние строки — ОБЩИЙ словарь скан-экранов портала, а не своя
  // раскраска прозрачностью. Ради него же заведён `over`: серостью «перебор»
  // не выражается вовсе, и строка сказала бы «готово» там, где на руках лишнее.
  const status: NeedStatus = row.over
    ? "over"
    : row.needed === 0
      ? "done"
      : row.picked > 0
        ? "partial"
        : "idle";
  return (
    <li className={`rounded border px-2 py-1.5 ${STATUS_ROW[status]}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-xs">
              {row.nomenclature_article || row.offer_id}
            </span>
            {row.characteristic_name && (
              <span className="rounded bg-slate-100 px-1 text-xs">
                {row.characteristic_name}
              </span>
            )}
            {row.needs_mark && <span className="rounded bg-slate-100 px-1 text-xs">ЧЗ</span>}
            {!row.nomenclature_ref && (
              <span className="rounded bg-destructive/10 px-1 text-xs text-destructive">
                нет в 1С
              </span>
            )}
          </div>
          {/* Наименование целиком: многоточие съедает как раз хвост, которым
              отличаются соседние позиции («…дуб» против «…цитрусовые»). */}
          {/* Имя от Ozon — запасное: у товара, не опознанного в 1С, своего
              наименования нет, и строка осталась бы без названия. */}
          <div className="break-words text-xs text-muted-foreground">
            {row.nomenclature_name || row.name}
          </div>
          <div className="text-xs text-muted-foreground">
            {row.postings} {plural(row.postings, ["коробка", "коробки", "коробок"])}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`text-lg font-semibold tabular-nums ${STATUS_TEXT[status]}`}>
            {row.over ? `+${row.over}` : row.needed === 0 ? "готово" : row.needed}
          </div>
          <div className="text-xs text-muted-foreground tabular-nums">
            {row.picked} из {row.total}
          </div>
        </div>
      </div>
    </li>
  );
}

/**
 * Блок «Отправления из нескольких товаров»: номер, срок, прогресс. Тап —
 * режим отправления. Отдельно от списка товаров: штуку для набора нельзя
 * положить «куда-нибудь» — начатая коробка, которую никто не заканчивает, хуже
 * отказа скана.
 */
function MultiPostingList({
  postings,
  onOpen,
}: {
  postings: ozon.OzonMultiPosting[];
  onOpen: (p: ozon.OzonMultiPosting) => void;
}): React.ReactElement {
  return (
    <section className="mt-4">
      <h2 className="mb-2 text-sm font-semibold">Отправления из нескольких товаров</h2>
      <ul className="space-y-2">
        {postings.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded border bg-white px-2 py-2 text-left"
              onClick={() => onOpen(p)}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">№ {p.posting_number}</div>
                <div className="text-xs text-muted-foreground">
                  {p.cutoff_at ? `собрать до ${fmtDateTime(p.cutoff_at)}` : "срок не задан"}
                </div>
              </div>
              <div className="shrink-0 text-right text-sm tabular-nums">
                собрано {p.units_picked} из {p.units_total}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Режим отправления: его строки и «Назад». Скан экрана в этом режиме уходит с
 * `posting_id` — только в строки этого отправления.
 *
 * ⚠️ Отправления может не оказаться в `multi_postings` (снято Ozon, собрано
 * вручную из «Коробок»): тогда так и пишем, а не рисуем пустой список —
 * пустота читалась бы как «всё набрано».
 */
function PostingMode({
  number,
  posting,
  onBack,
}: {
  number: string;
  posting: ozon.OzonMultiPosting | undefined;
  onBack: () => void;
}): React.ReactElement {
  return (
    <section className="mt-3">
      <div className="mb-2 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">Отправление № {number}</div>
          {posting && (
            <div className="text-xs text-muted-foreground tabular-nums">
              собрано {posting.units_picked} из {posting.units_total}
              {posting.cutoff_at && ` · до ${fmtDateTime(posting.cutoff_at)}`}
            </div>
          )}
        </div>
        <Button variant="outline" className="shrink-0" onClick={onBack}>
          <ArrowLeft className="mr-1 size-4" /> Назад
        </Button>
      </div>
      {!posting ? (
        <p className="rounded border bg-white px-2 py-3 text-sm text-muted-foreground">
          Этого отправления больше нет среди ждущих сборки — вернитесь к списку.
        </p>
      ) : (
        <ul className="space-y-2">
          {posting.lines.map((l, i) => {
            const status: NeedStatus =
              l.picked > l.quantity
                ? "over"
                : l.picked === l.quantity
                  ? "done"
                  : l.picked > 0
                    ? "partial"
                    : "idle";
            return (
              // ⚠️ Ключ с индексом: у строк с неопознанным товаром ключевые
              // поля совпадают.
              <li key={`${l.offer_id}-${i}`} className={`rounded border px-2 py-1.5 ${STATUS_ROW[status]}`}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-mono text-xs">{l.nomenclature_article || l.offer_id}</span>
                      {l.characteristic_name && (
                        <span className="rounded bg-slate-100 px-1 text-xs">{l.characteristic_name}</span>
                      )}
                      {l.needs_mark && <span className="rounded bg-slate-100 px-1 text-xs">ЧЗ</span>}
                      {!l.nomenclature_ref && (
                        <span className="rounded bg-destructive/10 px-1 text-xs text-destructive">
                          нет в 1С
                        </span>
                      )}
                    </div>
                    <div className="break-words text-xs text-muted-foreground">
                      {l.nomenclature_name || l.name}
                    </div>
                  </div>
                  <div className={`shrink-0 text-lg font-semibold tabular-nums ${STATUS_TEXT[status]}`}>
                    {l.picked} / {l.quantity}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Строка отправления: номер, срок — и ТОВАР, который надо взять.
 *
 * ⚠️ Раньше здесь был один номер отправления. Кладовщику он не говорит ничего:
 * `44903551-0320-1` не подсказывает, что нести с полки, а именно за этим он на
 * экран и смотрит. У ВБ то же место занимает артикул с наименованием, и это не
 * оформление — по нему ищут товар.
 *
 * Наименование ЦЕЛИКОМ, переносом по словам: многоточие съедает как раз хвост,
 * которым отличаются соседние позиции («…дуб» против «…цитрусовые»).
 */
function PostingRow({
  posting,
  onShip,
  onLabel,
  onReplace,
  replaceBusy,
  busy,
}: {
  posting: ozon.OzonPosting;
  // Пропы обязательные: единственный вызов передаёт их все, а «строка без
  // кнопок» не встречается — необязательность обещала бы режим, которого нет.
  onShip: () => void;
  onLabel: () => void;
  /** «Заменить код»: снять скан, код которого Ozon не принял. */
  onReplace: (pickId: string) => void;
  replaceBusy: boolean;
  busy: boolean;
}): React.ReactElement {
  const assembled = !!posting.status && posting.status !== "awaiting_packaging";
  return (
    <li
      className={`rounded border bg-white px-2 py-1.5 ${posting.is_complete ? "opacity-50" : ""}`}
    >
      <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
        <span className="truncate">{posting.posting_number}</span>
        <span className="shrink-0 tabular-nums">
          {posting.units_picked} из {posting.units_total} шт
        </span>
      </div>

      {/* Шаги идут строго по порядку: этикетки до сборки у Ozon НЕ
          СУЩЕСТВУЕТ — обе ручки площадки отвечают 400. Поэтому «Напечатать»
          появляется только после сборки, а не гаснет с непонятной ошибкой. */}
      <div className="mt-1.5 flex gap-2">
        {/* ⚠️ «Собрано» решает СТАТУС, а не `shipped_at`. Отметка означает
            «сборка НАЧАТА» — она ставится до обращения к площадке ради
            идемпотентности. Читая её, экран после таймаута рисовал только
            «Этикетка», и кнопки «Собрать», к которой отсылает сам текст
            ошибки, в разметке не оставалось вовсе. */}
        {!assembled ? (
            <Button
              size="sm"
              className="h-9 flex-1"
              disabled={!posting.is_complete || busy}
              onClick={onShip}
            >
              <PackageCheck className="mr-1 size-4" />
              {/* Две разные беды не должны выглядеть одинаково: «не хватает
                  сканов» чинится сканированием, а «товара нет в 1С» — нет,
                  и кладовщик иначе будет носить код, который не совпадёт. */}
              {posting.has_unresolved
                ? "Нет в 1С"
                : posting.is_complete
                  ? posting.shipped_at
                    ? "Собрать ещё раз"
                    : "Собрать"
                  : "Не набрано"}
            </Button>
        ) : (
          <>
            <Button
              size="sm"
              variant={posting.label_printed_at ? "outline" : "default"}
              className="h-9 flex-1"
              disabled={busy}
              onClick={onLabel}
            >
              <Printer className="mr-1 size-4" />
              {posting.label_printed_at ? "Печатать ещё" : "Этикетка"}
            </Button>
            {/* ⚠️ Кнопки «Документы 1С» здесь БОЛЬШЕ НЕТ. Комплект выписывается
                на ПОСТАВКУ целиком, и кнопка у отдельной коробки закрывала бы
                всю поставку — необратимо и не тем действием, которое обещала
                подпись. Документы живут на своём экране («Поставка» в
                аппбаре); здесь остаётся то, что и правда про эту коробку:
                собрать и напечатать этикетку. */}
            {posting.documents.length > 0 && (
              <span className="flex h-9 flex-1 items-center justify-center gap-1 rounded border text-xs text-muted-foreground">
                <FileText className="size-4" />
                Документы выписаны
              </span>
            )}
          </>
        )}
      </div>

      {posting.items.map((it, i) => (
        // ⚠️ Ключ по индексу намеренно: у строки с неопознанным SKU он равен
        // нулю, и `key={it.sku}` дал бы дубли ровно на тех строках, ради
        // которых предупреждение и заведено.
        <div key={`${it.sku}-${i}`} className="mt-1 flex items-start gap-2 text-sm">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-mono text-xs">
                {it.nomenclature_article || it.offer_id}
              </span>
              {it.characteristic_name && (
                <span className="rounded bg-slate-100 px-1 text-xs">
                  {it.characteristic_name}
                </span>
              )}
              {it.needs_mark && (
                <span className="rounded bg-slate-100 px-1 text-xs">ЧЗ</span>
              )}
              {!it.nomenclature_ref && (
                <span className="rounded bg-destructive/10 px-1 text-xs text-destructive">
                  нет в 1С
                </span>
              )}
            </div>
            <div className="break-words text-xs text-muted-foreground">
              {it.nomenclature_name || it.name}
            </div>
            {/* После сборки код уже не заменить — и ошибок там не бывает. */}
            {!assembled && (
              <MarkErrorList
                errors={it.mark_errors}
                onReplace={onReplace}
                busy={replaceBusy}
              />
            )}
          </div>
          <span className="shrink-0 tabular-nums font-medium">
            {it.picked} / {it.quantity}
          </span>
        </div>
      ))}
    </li>
  );
}

/**
 * Уточнение: чего резолверу не хватило, чтобы записать скан.
 *
 * ⚠️ Уточнение — НЕ отказ. Раньше экран рисовал его красной плашкой с пустым
 * текстом, и ответить было нечем: кладовщик упирался в тупик на каждом таком
 * товаре.
 *
 * ⚠️ Характеристика и партия спрашиваются ОДНИМ экраном: оба ответа человек
 * знает в один момент — товар у него в руках. Так же сделано в приёмке
 * возвратов и коробках.
 *
 * ⚠️ «Принять без партии» — не удобство, а ВЫХОД ИЗ ТУПИКА: партия напечатана
 * на этикетке, но её нет в 1С, и без этой кнопки человек вводит номер, получает
 * «партия не найдена», вводит снова — и так до бесконечности.
 *
 * ⚠️ Enter в поле партии обязателен: номер вводят СКАНЕРОМ с этикетки, а
 * сканер дописывает Enter. Без него поле не заполнить сканером вовсе.
 */
function ClarifySheet({
  result,
  onAnswer,
}: {
  result: ozon.OzonScanResult;
  onAnswer: (patch: Partial<ozon.OzonScanPayload>) => void;
}): React.ReactElement {
  const [batch, setBatch] = useState("");
  const bothNeeded = result.needs_characteristic && result.needs_partia;
  const [charRef, setCharRef] = useState("");

  const submitPartia = (withoutPartia: boolean) =>
    onAnswer({
      batch: withoutPartia ? "" : batch,
      accept_without_partia: withoutPartia,
      ...(charRef ? { characteristic_ref: charRef } : {}),
    });

  return (
    <BottomSheet onClose={() => undefined}>
      <div className="space-y-3 p-3">
        <div className="font-medium">Уточните: {result.nomenclature_name || "товар"}</div>
        {result.message && <div className="text-sm text-muted-foreground">{result.message}</div>}

        {result.needs_characteristic && (
          <div>
            <div className="mb-1 text-xs font-semibold text-amber-700">Характеристика:</div>
            {result.characteristic_options.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                1С не вернула ни одного варианта — разберитесь в справочнике.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {result.characteristic_options.map((o) => (
                  <Button
                    key={o.ref}
                    variant={charRef === o.ref ? "default" : "outline"}
                    className="h-11"
                    onClick={() =>
                      // Нужны оба ответа — копим; нужен только этот — шлём сразу.
                      bothNeeded ? setCharRef(o.ref) : onAnswer({ characteristic_ref: o.ref })
                    }
                  >
                    {/* ⚠️ Подпись по ССЫЛКЕ, а не по имени: у настоящей
                        характеристики имя бывает пустым, и по нему она
                        подписалась бы «без характеристики». */}
                    {o.ref === NO_CHARACTERISTIC ? "Без характеристики" : o.name}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {result.needs_partia && (
          <div>
            <div className="mb-1 text-xs font-semibold text-amber-700">Партия:</div>
            <div className="flex gap-2">
              <input
                autoFocus
                className="h-11 flex-1 rounded-md border px-2 text-sm"
                value={batch}
                inputMode="text"
                placeholder="номер партии"
                onChange={(e) => setBatch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && batch.trim()) submitPartia(false);
                }}
              />
              <Button className="h-11" disabled={!batch.trim()} onClick={() => submitPartia(false)}>
                Принять
              </Button>
            </div>
            <Button
              variant="ghost"
              className="mt-2 h-11 w-full"
              onClick={() => submitPartia(true)}
            >
              Принять без партии
            </Button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

/**
 * SH-FIX «Разбор» Ozon — отправления, застрявшие между сборкой и учётом.
 *
 * Почему раздел существует и почему причина выводится, а не хранится —
 * `backend/src/wh_helper/ozon_fbs/fixups.py`. Здесь только экран.
 *
 * ⚠️ Чинит ТЕМИ ЖЕ мутациями, что и карточка отправления: они приходят
 * пропсами. Своя четвёртая была копией той же тройки — с одним общим тостом
 * вместо трёх точных и, главное, без записи ответа в кеш: результат починки
 * отбрасывался, и уже открытая шторка «Коробки» узнавала о ней только
 * перезапросом.
 *
 * ⚠️ Список — СВОИМ запросом, а не из `pick-state`: он нужен, только когда
 * шторку открыли, а состояние экрана перечитывается после каждого скана.
 * Оттуда приезжает лишь бейдж — одно число.
 */
function FixSheet({
  cabinetId,
  repair,
  busy,
  onClose,
}: {
  cabinetId: number;
  repair: Record<RepairableIssue, (row: ozon.OzonIssue) => void>;
  busy: boolean;
  onClose: () => void;
}): React.ReactElement {
  const issuesQ = useQuery({
    queryKey: ["ozon", "issues", cabinetId],
    queryFn: () => ozon.listIssues(cabinetId),
  });

  const rows = issuesQ.data ?? [];
  return (
    <BottomSheet onClose={onClose} panelClassName="max-h-[75vh] overflow-y-auto p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Разбор</h2>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Закрыть
        </Button>
      </div>

      {issuesQ.isLoading || issuesQ.isError ? (
        <LoadState
          isLoading={issuesQ.isLoading}
          error={issuesQ.error}
          onRetry={() => void issuesQ.refetch()}
        />
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Ничего не застряло — всё доехало до учёта.
        </p>
      ) : (
        <ul className="divide-y">
          {rows.map((row) => {
            // ⚠️ Отсутствие кода в `repair` И ЕСТЬ правило «кнопки нет»,
            // записанное один раз. Раньше их было два — тернарник, у которого
            // `blocked` проваливался в «создать документы», и отдельная
            // проверка в разметке; разъехаться они могли молча.
            const fix = repair[row.issue as RepairableIssue];
            return (
              <li key={row.posting_id} className="py-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{row.title}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {row.posting_number} · {row.units} шт
                    </div>
                  </div>
                  {/* У «собрать нечем» кнопки нет: повтор упрётся в ту же
                      причину, чинят её в 1С или пересканом. */}
                  {fix && (
                    <Button
                      size="sm"
                      variant="outline"
                      // ⚠️ Без поставки кнопка «К поставке» вела бы в никуда:
                      // включённая кнопка, которая молча ничего не делает,
                      // хуже отсутствующей.
                      disabled={
                        busy || (row.issue === "no_documents" && !row.supply_id)
                      }
                      onClick={() => fix(row)}
                    >
                      {/* ⚠️ «Документы» — это ПЕРЕХОД на экран завершения, а не
                          починка на месте: комплект выписывается на поставку
                          целиком, и «Повторить» у одной строки означало бы
                          закрыть всю поставку — необратимо и не тем действием,
                          которое обещает подпись. */}
                      {FIX_LABEL[row.issue as RepairableIssue]}
                    </Button>
                  )}
                </div>
                {row.details.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-xs text-destructive">
                    {row.details.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </BottomSheet>
  );
}
